import * as React from 'react'

import { EllipsisIcon, RefreshCwIcon, Trash2Icon } from 'lucide-react'
import { observer } from 'mobx-react'

import { WorkflowStepType } from '~/client/graph'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import { getWorkflowStepDisplayName } from '~/client/src/shared/localization/enumDisplayTexts'
import Localization from '~/client/src/shared/localization/LocalizationManager'

// localization: translated

interface IProps {
  stepType: WorkflowStepType
  stepIdx: number
  isRequestOrSubmitStep: boolean

  isAutomaticStep?: boolean

  onReplaceStep(index: number, stepType: WorkflowStepType): void
  onRemoveStep(index: number): void

  onAddStep?(index: number, stepType: WorkflowStepType): void
}

@observer
export default class WorkflowStepMenuModal extends React.Component<
  IProps,
  { isOpen: boolean }
> {
  public state = { isOpen: false }

  public render() {
    return (
      <Popover open={this.state.isOpen} onOpenChange={this.setOpen}>
        <PopoverTrigger
          nativeButton={false}
          render={
            <span className="no-grow pointer full-height tw:flex tw:items-center pa8 brada24 tw:data-popup-open:bg-accent" />
          }
        >
          <EllipsisIcon className="tw:size-4" />
        </PopoverTrigger>
        <PopoverContent
          side="bottom"
          align="start"
          className="tw:w-auto tw:overflow-hidden tw:p-0"
        >
          {this.moreMenuModalContent}
        </PopoverContent>
      </Popover>
    )
  }

  private get moreMenuModalContent(): JSX.Element {
    const { isAutomaticStep, isRequestOrSubmitStep } = this.props
    const isNonDeletable = isAutomaticStep || isRequestOrSubmitStep

    return (
      <div
        className="tw:flex tw:flex-col px16 h40 pointer nowrap"
        onClick={this.close}
      >
        {isNonDeletable && (
          <div
            className="tw:flex tw:items-center full-height"
            onClick={this.onReplaceStepClick}
          >
            <RefreshCwIcon className="tw:mr-2.5 tw:size-4 tw:shrink-0" />
            <span className="tw:flex-1">{this.replaceStepText}</span>
          </div>
        )}
        {!isNonDeletable && (
          <div
            className="tw:flex tw:items-center text large red full-height"
            onClick={this.onRemoveStepClick}
          >
            <Trash2Icon className="tw:mr-2.5 tw:size-4 tw:shrink-0" />
            <span className="tw:flex-1">
              {Localization.translator.workflowConfDescr.deleteWorkflowStep}
            </span>
          </div>
        )}
      </div>
    )
  }

  private setOpen = (isOpen: boolean) => this.setState({ isOpen })

  private close = () => this.setOpen(false)

  private get replaceStepText(): string {
    const { isAutomaticStep } = this.props
    const { replaceWithStep, replaceWithStart } =
      Localization.translator.workflowConfDescr

    return isAutomaticStep
      ? replaceWithStart
      : replaceWithStep(getWorkflowStepDisplayName(this.replaceStepType))
  }

  private get replaceStepType(): WorkflowStepType {
    return this.props.stepType === WorkflowStepType.Request
      ? WorkflowStepType.Submission
      : WorkflowStepType.Request
  }

  private onReplaceStepClick = () => {
    const { stepIdx, onReplaceStep, isAutomaticStep, onAddStep } = this.props

    if (isAutomaticStep) {
      return onAddStep(stepIdx, WorkflowStepType.Start)
    }

    onReplaceStep(stepIdx, this.replaceStepType)
  }

  private onRemoveStepClick = () => {
    this.props.onRemoveStep(this.props.stepIdx)
  }
}
